import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Grid, Card, CardContent } from "@deere/fuel-react";
import SelfServiceButtons from "../SelfServiceButtons";
import { getActiveSurveys, getSelfServiceSurveys } from "../../../../services/campaign.service";
import { getSurveyTemplates, getAvailableSurveyCodes, getSurveyLiterals, getSelfServiceTemplateBySurveyCode } from "../../../../services/survey.service";
import { useOktaAuth } from "@okta/okta-react";
import { generateSurveyCode, prepareSurveyData, removeUnwantedProperties } from "../../../../utils/utils";
import { ALL, CRS_SURVEY_TEMPLATES, DEFAULT_PART_SECTION_PAYLOAD, FETCHING_DATA, LBK_OK, LBL_ERROR, SELFSERVICE_DEFAULT_PAYLOAD, STATUS_DRAFT, SURVEY_NAME_LBL, VALUE_NEW, VALUE_REVISION, VALUE_COPY, SURVEY_COPY_LBL, CODE_SURVEY_LANGUAGE, SURVEY_NAME_FIELD, SAVING_DATA } from "../../../../utils/surveyLiterals";
import { ERR_DATA_FETCH, ERR_EXISTING_REVISION } from "../../../../utils/errorLiterals";
import { useUserInfo } from "../../../../providers/userProvider";
import { getSuppSurveyConfiguration } from "../../../../services/userService";
import { useLocation } from "react-router-dom";
import { GenericFuelModal } from "../../../../components/modals/GenericFuelModal";
import { CreateSurveyRadio, SurveyNameSelector, SurveyNameTextField, TemplateTypeSelection } from "../SelfServiceInputs";
import SelfServiceLoader from "../SelfServiceLoader";


const Step1Card = ({ fieldValidation, setFieldValidation, currentStep, activeStep, setActiveStep, payloadData, setPayloadData, emailTemplateBody }) => {
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const location = useLocation();
  const { authState } = useOktaAuth();
  const [surveyCodes, setSurveyCodes] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [surveysByTemplate, setSurveysByTemplate] = useState([]);
  const [loader, setLoader] = useState({isLoading: true, message: FETCHING_DATA});
  const isRevisionFromLandingPage = location?.state?.payloadData?.editSurvey !== undefined ? location.state.payloadData.editSurvey : false;
  const [createSurveyOption, setCreateSurveyOption] = useState(isRevisionFromLandingPage ? VALUE_REVISION : VALUE_NEW);
  const [allSurveys, setAllSurveys] = useState([]);
  const [allActiveSurveys, setAllActiveSurveys] = useState([]);
  const [activeSurveysByTemplate, setActiveSurveysByTemplate] = useState([]);
  const [showRevisionErrorModal, setShowRevisionErrorModal] = useState(false);
  const [surveyNameField, setSurveyNameField] = useState(payloadData?.description ?? "");
  const [surveyNamePicker, setSurveyNamePicker] = useState(payloadData?.id ?? location?.state?.surveyId ?? 0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let isError = false;
        if (!payloadData.id) {// If payloadData.id is available, we don't need to fetch survey codes
          const surveyCd = await getAvailableSurveyCodes(authState.accessToken.accessToken);
          surveyCd && Array.isArray(surveyCd) ? setSurveyCodes(surveyCd) : isError = true;
        }
        const templates = await getSurveyTemplates(authState.accessToken.accessToken);
        templates && Array.isArray(templates) ? setTemplates(templates) : isError = true;
        const surveys = await getSelfServiceSurveys(authState.accessToken.accessToken);
        if (surveys && Array.isArray(surveys)) {
          setAllSurveys(surveys);
          setSurveysByTemplate(surveys.filter((survey) => survey.templateType === payloadData.templateType && survey.active));
        } else isError = true;
        const activeSurveys = await getActiveSurveys(authState.accessToken.accessToken);
        if (activeSurveys && Array.isArray(activeSurveys)) {
          setAllActiveSurveys(activeSurveys);
          setActiveSurveysByTemplate(activeSurveys.filter((survey) => survey.templateType === payloadData.templateType));
        } else isError = true;
        isError ? setLoader({isLoading: false, message: ERR_DATA_FETCH}) : setLoader({isLoading: false, message: ""});
      } catch (err) {
        console.log(err);
        setLoader({isLoading: false, message: ERR_DATA_FETCH});
      }
    };
    fetchData();
  }, [payloadData.id]);


  /*********************************Input Change Handler Methods Start*********************************/

  //Handler for Survey Creation Options Radio Buttons
  const isNewSurveyChangeHandler = (value) => {
    setCreateSurveyOption(value);
    setPayloadData(SELFSERVICE_DEFAULT_PAYLOAD);
    setSurveyNameField("");
    setSurveyNamePicker(0);
  }

  //Handler for Template Dropdown
  const templateChangeHandler = (e) => {
    setSurveysByTemplate(allSurveys.filter((survey) => survey.templateType === e.target.value && survey.active));
    setActiveSurveysByTemplate(allActiveSurveys.filter((survey) => survey.templateType === e.target.value));
    setPayloadData(() => {
      let data;
      const matchingTemplate = templates?.find((template) => template.templateType === e.target.value);
      if (matchingTemplate) {
        data = matchingTemplate.templateContextPath;
      }
      return {
        ...SELFSERVICE_DEFAULT_PAYLOAD,
        templateType: e.target.value,
        homeContextPath: data,
        surveyNotification: emailTemplateBody,
      };
    });
    setSurveyNameField("");
    setSurveyNamePicker(0);
  };

  //Handler for Survey name Text Field & Survey Picker Dropdown 
  const surveyNameChangeHandler =  (e) => {
    setFieldValidation(false);
    if (e.target.name === SURVEY_NAME_FIELD) {
      setSurveyNameField(e.target.value);
    } else {
      setSurveyNamePicker(e.target.value);
      setPayloadData(SELFSERVICE_DEFAULT_PAYLOAD);
    }
  };

  /*********************************Input Change Handler Methods End**********************************/

  /***************************************Utility Methods Start***************************************/

  //This method sets default sections in case when Template Type = Part related
  const setDefaultSections = (templateType, code) => {
    const sections = [];
    if (templateType === CRS_SURVEY_TEMPLATES.PART) {
      DEFAULT_PART_SECTION_PAYLOAD.forEach((sec) => {
        sections.push({
          description: sec.description,
          active: true,
          code: code + "_SEC" + sec.showOrder,
          showOrder: sec.showOrder,
          longDescription: "",
          questions: [],
          type: sec.type,
          translations: [],
          links: [],
          isDefault: sec.isDefault,
        });
      });
    }
    return sections;
  };

  //This method validates the input field's data before moving on to the next step
  const fnValidate = (publishHandler) => {
    let isValid = true;
    switch (createSurveyOption) {
      case VALUE_NEW:
        (!surveyNameField || surveyNameField === "") && (isValid = false);
        break;
      case VALUE_REVISION:
        (!surveyNamePicker || surveyNamePicker === 0) && (isValid = false);
        break;
      case VALUE_COPY:
        (!surveyNameField || surveyNameField === "" || !surveyNamePicker || surveyNamePicker === 0) && (isValid = false);
        break;
    }
    !isValid ? setFieldValidation(!isValid) : decideNext(publishHandler);
  };

  //This method decides next action
  const decideNext = (publishHandler) => {
    setLoader({isLoading: true, message: SAVING_DATA});
    const comingFromLanding = Boolean(payloadData?.id);
    // Coming from survey creation (step 1): build payload from user inputs
    if (!comingFromLanding) {
      setupPayload(publishHandler);
      return;
    }
    // Coming from landing page but NOT a revision: it's a survey edit flow
    if (!isRevisionFromLandingPage) {
      const newPayload = {...payloadData, description: surveyNameField}
      setPayloadData(newPayload);
      publishHandler(newPayload);
      return;
    }
    // Coming from landing page AND is a revision
    // If user hasn't changed anything (payload still has id), go to next step
    if (payloadData?.id) {
      publishHandler(payloadData);
      return;
    } 
    // User changed something → payload was reset → we need to set it up again
    setupPayload(publishHandler);
  };

//This method builds payload to save
  const setupPayload = async (publishHandler) => {
    if (createSurveyOption === VALUE_NEW) {
      const code = generateSurveyCode(surveyNameField, surveyCodes);
      const newPayload = { ...payloadData, code: code, sections: setDefaultSections(payloadData.templateType, code), description: surveyNameField, adminRole: `${code}_ADMIN` };
      setPayloadData(newPayload);
      publishHandler(newPayload);
    } else {
      try {
        const surveyData = await getSuppSurveyConfiguration(surveyNamePicker, ALL, authState.accessToken.accessToken);
        const literalData = await getSurveyLiterals(surveyNamePicker, null, authState.accessToken.accessToken);
        const notificationData = await getSelfServiceTemplateBySurveyCode(surveyData.code, authState.accessToken.accessToken);
        if (surveyData && literalData && notificationData) {
          //Checking if there is a existing revision in process
          let existingDraftSurveyIdx = -1;
          if (createSurveyOption === VALUE_REVISION) {
            const surveysByCode = allSurveys ? allSurveys.filter((s) => s.code === surveyData.code) : [];
            existingDraftSurveyIdx = surveysByCode.findIndex((s) => s.templateStatus === STATUS_DRAFT && Number(s.templateRevision) > Number(surveyData.templateRevision));
          }
          if (existingDraftSurveyIdx > -1) {
            setShowRevisionErrorModal(true);
          } else {
            const data = prepareSurveyData(surveyData, literalData, notificationData);
            if (data) {
              removeUnwantedProperties(data);
              data.editSurvey = createSurveyOption === VALUE_REVISION;
              data.active = false;
              data.templateStatus = STATUS_DRAFT;
              data.templateRevision = createSurveyOption === VALUE_REVISION ? (Number(data.templateRevision) + 1).toString() : "1";
              setPayloadData(data);
            }
            createSurveyOption === VALUE_REVISION ? publishHandler(data) : updatePayloadForCopiedSurvey(publishHandler);
          }
        }
      } catch (err) {
        console.log(err);
        setLoader({isLoading: false, message: ERR_DATA_FETCH});
      }      
    }
  };

  const updatePayloadForCopiedSurvey = (publishHandler) => {
    const code = generateSurveyCode(surveyNameField, surveyCodes);
    let newPayload = null;
    setPayloadData((prevState) => {
      const newState = { ...prevState, description: surveyNameField, code, helpDocs: [], adminRole: `${code}_ADMIN` };
      const newLanguages = newState.language.map(lang => ({ ...lang, code: `${code}_${CODE_SURVEY_LANGUAGE}` }));
      const newTranslations = newState.translations.map(trn => ({ ...trn, text: "" }));

      const newSections = (newState.sections ?? [])
        .slice()
        .sort((a, b) => (a?.showOrder ?? 0) - (b?.showOrder ?? 0))
        .map((sec, secIdx) => {
          const newQuestions = (sec.questions ?? [])
            .slice()
            .sort((a, b) => (a?.showOrder ?? 0) - (b?.showOrder ?? 0))  
            .map((que, queIdx) => {
              const newSelections = (que.selections ?? [])
                .slice()
                .sort((a, b) => (a?.showOrder ?? 0) - (b?.showOrder ?? 0))
                .map((sel, selIdx) => ({ ...sel, showOrder: selIdx + 1, code: `${code}_SEC${secIdx + 1}_Q${queIdx + 1}_S${selIdx + 1}` }));
              return { ...que, showOrder: queIdx + 1, code: `${code}_SEC${secIdx + 1}_Q${queIdx + 1}`, selections: newSelections };
            });
          return { ...sec, showOrder: secIdx + 1, code: `${code}_SEC${secIdx + 1}`, questions: newQuestions };
        });
      newPayload = { ...newState, sections: newSections, language: newLanguages, translations: newTranslations };
      return newPayload;
    });
    publishHandler(newPayload);
  };

  //This method gets the value for survey name picker dropdown in Revision scenario
  const getSurveyPickerValue = (surveysByTemplate) => {
    //Return immediately of value = 0
    if (surveyNamePicker === 0) return surveyNamePicker;

    //If matches by ID return the same value of surveyNamePicker
    const surveyIndex = surveysByTemplate?.findIndex((survey) => survey.id === surveyNamePicker);
    if (surveyIndex > -1) return surveyNamePicker;
    
    //Trying to match by survey name if ID match not found 
    //In case of Revision the survey name is going to be the same for new version hence this match is valid to find the id
    const surveyByNameIdx = surveysByTemplate?.findIndex((survey) => survey.description === payloadData?.description);
    if (surveyByNameIdx > -1) return surveysByTemplate[surveyByNameIdx].id;
    
    //Fallback
    return surveyNamePicker;
  };

  /****************************************Utility Methods End****************************************/

  return (
    <Card>
      <CardContent>
        {loader.message !== "" ? (
          <SelfServiceLoader loader={loader} />
        ) : (
          <Grid container direction="column" justifyContent="space-between" minHeight={"60vh"} rowSpacing={4}>
            <Grid item>
              <Typography variant="h1">
                {currentStep.step} - {currentStep.title}
              </Typography>
            </Grid>
            <Grid item pl={5} pr={5}>
              <CreateSurveyRadio value={createSurveyOption} changeHandler={isNewSurveyChangeHandler}/>
              <br />
              <br />
              <TemplateTypeSelection value={payloadData.templateType} changeHandler={templateChangeHandler} templates={templates} />
              <br />
              <br />
              {createSurveyOption === VALUE_NEW && <SurveyNameTextField value={surveyNameField} errorFlag={fieldValidation} changeHandler={surveyNameChangeHandler} />}
              {createSurveyOption === VALUE_REVISION && <SurveyNameSelector label={SURVEY_NAME_LBL} value={getSurveyPickerValue(surveysByTemplate)} errorFlag={fieldValidation} changeHandler={surveyNameChangeHandler} surveyList={surveysByTemplate} />}
              {createSurveyOption === VALUE_COPY && (
                <>
                  <SurveyNameSelector label={SURVEY_COPY_LBL} value={surveyNamePicker} errorFlag={fieldValidation} changeHandler={surveyNameChangeHandler} surveyList={activeSurveysByTemplate} />
                  <br />
                  <br />
                  <SurveyNameTextField value={surveyNameField} errorFlag={fieldValidation} changeHandler={surveyNameChangeHandler} />
                </>
              )}
            </Grid>
            <Grid item pr={5}>
              <Grid container justifyContent="flex-end">
                <SelfServiceButtons
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  fnValidate={fnValidate}
                  payloadData={payloadData}
                  setPayloadData={setPayloadData}
                  setLoader={setLoader}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </CardContent>
        <GenericFuelModal
          title={LBL_ERROR}
          message={ERR_EXISTING_REVISION}
          show={showRevisionErrorModal}
          handleClose={() => setShowRevisionErrorModal(false)}
          primaryButton={LBK_OK}
          handleSave={() => setShowRevisionErrorModal(false)}
        />
    </Card>
  );
};

export default Step1Card;
